import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
} from "@mui/material";
import { GridContainer, Grid } from "src/shared/grid";
import { getCategoryByType, getCodes, getUniqueFuels } from "src/apis/getFuelSampleCalls";
import { useStoreContext } from "src/store/context";
import { useAddAlert, useUser, useFSMapStartDate, useFSMapEndDate } from "src/store/actions";
import { ValidCharacters, errorResponse, validateCharacters } from "src/store/common";
import { StyledTableCellBinary, StyledTableRowBinary } from "src/shared/Styles/Styles";
import { alertMessages } from "src/constants";
import moment from "moment-timezone";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

interface Data {
  fuel_type: string;
  category: string;
  sub_category: string;
  method_type: string;
  sample: string;
}
type Order = 'asc' | 'desc';



function createData(
  fuel_type: string,
  category: string,
  sub_category: string,
  method_type: string,
  sample: string,
): Data {
  return {
    fuel_type,
    category,
    sub_category,
    method_type,
    sample,
  };
}

type FuelTableProps = {
  handleAll: (event: any, categoryNewValue: any, subCategoryNewValue: any, fuelTypeValue: any, methodValue: any) => void;
  handleClose: () => void;
}

const FuelTable: React.FC<FuelTableProps> = ({
  handleAll,
  handleClose
}
) => {
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof Data>('fuel_type');
  const [page, setPage] = useState(0);
  const [state, dispatch] = useStoreContext();
  const addAlert = useAddAlert(dispatch);
  const setFSMapStartDate = useFSMapStartDate(dispatch);
  const setFSMapEndDate = useFSMapEndDate(dispatch);
  const allCodeObj = useMemo(() => ({ code_key: "All", code_table_id: -1, code_value: 'All', type: 'All' }), []);
  const [sampleMethods, setSampleMethods] = useState<any[]>([allCodeObj]);
  const [method, setMethod] = useState<string | undefined>(sampleMethods[0].code_key);
  const [inputMethod, setInputMethod] = useState('');
  const [sampleSubCats, setSampleSubCats] = useState<any[]>([allCodeObj]);
  const [subCategory, setSubCategory] = useState<string | undefined>(sampleSubCats[0].code_key);
  const [inputSubCategory, setInputSubCategory] = useState('');
  const [sampleCategories, setSampleCategories] = useState<any[]>([allCodeObj]);
  const [filteredCategories, setFilteredCategories] = useState<any[]>([allCodeObj]);
  const [category, setCategory] = useState<string | undefined>(sampleCategories[0].code_key);
  const [inputCategory, setInputCategory] = useState('');
  const [filteredTypes, setFilteredTypes] = useState<any[]>([]);
  const [types, setTypes] = useState<any[] | undefined>([]);
  const [inputType, setInputType] = useState('');
  const [samples, setSamples] = useState<Data[]>([]);
  const [, setSamplesCallComplete] = useState<boolean>(true);
  const [token, setToken] = useState<string | null>(null);
  const setUser = useUser(dispatch);
  const [errorStartDate, setErrorStartDate] = useState<string | null>(null);
  const [errorEndDate, setErrorEndDate] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<any >(state.fsMapStartDate ? moment(state.fsMapStartDate) : null);
  const [endDate, setEndDate] = useState<any >(state.fsMapEndDate ? moment(state.fsMapEndDate) : null);
  const [selectedFuel, setSelectedFuel] = useState<Data | null>(null);

  /* this will get the fuel type and category so they are filtered on the dropdown*/
  const toGetCategory = useCallback((type?: string, cat?: string, getAll?: boolean) => {
    (async () => {
      try {
        getAll = getAll ? getAll : false;
        const responseArray: any = await getCategoryByType(null, addAlert, type, cat, getAll);
        if (responseArray) {
          let uniqueCategories = [...new Set(responseArray.map((r: any) => r.category))];
          let uniqueTypes = [...new Set(responseArray.map((r: any) => r))];
          if (getAll) {
            setFilteredTypes(uniqueTypes);
            uniqueCategories.unshift(allCodeObj.code_key);
            setFilteredCategories(uniqueCategories);
          }
          else if (cat) {
            setFilteredTypes(uniqueTypes);
          }
          else {
            uniqueCategories.unshift(allCodeObj.code_key);
            setFilteredCategories(uniqueCategories);
          }

        }
        else {
          setFilteredCategories([]);
          setFilteredTypes([])
        }
      }
      catch (error) {
        errorResponse(error, addAlert, alertMessages.fieldSampleHomeData);
      }

    })();
  }, [addAlert, allCodeObj]);

  const handleRowClick = (row: Data) => {
    setSelectedFuel(row); // Set the clicked row as selected
  };


  const isRowSelected = (row: Data) => {
    return selectedFuel === row;
  };

  const handleRequestSort = (
    event: React.MouseEvent<unknown>,
    property: keyof Data,
  ) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };


  /* this use effect makes the call to get codes to update the dropdowns*/
  useEffect(() => {
    if (sampleMethods && sampleMethods.length < 2) {
      (async () => {
        try {
          const requestVars = {
            returnAll: true,
            type: null
          }
          const codeResponse: any = await getCodes(requestVars);
          toGetCategory('All', 'All', true);
          const cats = codeResponse.cats;
          cats.unshift(allCodeObj);
          const subCats = codeResponse.subCats;
          subCats.unshift(allCodeObj)
          const methods = codeResponse.methods;
          methods.unshift(allCodeObj)
          setSampleCategories(cats);
          setSampleSubCats(subCats);
          setSampleMethods(methods);
        }
        catch (error) {
          errorResponse(error, addAlert, alertMessages.fieldSampleHomeData);
        }
      })();
    }
  }, [addAlert, allCodeObj, category, sampleMethods,
    method, subCategory, types, toGetCategory
  ]);


  const fuelSampleResponse = useCallback((request: any) => {
    (async () => {
      try {
        const fuelSampleResponse: any = await getUniqueFuels(token, addAlert, request);
        const samples = fuelSampleResponse.uniqueFuelSamples;
        setSamplesCallComplete(true);
        if (samples.length > 0) {
          let dataArray: Data[] = [];
          samples.forEach((sample: any) => {
            sample.formatted_date = moment.utc(sample.latestSampleDate).utcOffset(state.userTimeZone.offset).format('MM/DD/YYYY');
            dataArray.push(createData(
              sample.fuelType,
              sample.category,
              sample.subCategory,
              sample.method,
              sample.formatted_date
            ));
          });
          setSamples(dataArray);
        }
        else {
          setSamples([]);
        }
      } catch (error) {
        setSamplesCallComplete(true);
        setSamples([]);
        console.log('error', error)
      }
    })();
  }, [addAlert, token, state.userTimeZone.offset]);


  useEffect(() => {
    const request = {
      startDate,
      endDate,
      category: category,
      subCategory: subCategory,
      method: method,
      status: "Submitted",
      fuel: types?.[0]?.fuel_type || 'ALL',
      sortBy: orderBy,
      sortOrder: order,
      page: page,
      perPage: null,
    }

    fuelSampleResponse(request);
  }, [types, category, subCategory, method, page, order, orderBy, fuelSampleResponse,
    endDate, startDate,
  ]);

  useEffect(() => {
    if (state.userTokens && token) {
      /** If the Token is refreshed this will set the @param token equal to updated value */
      setToken(state.userTokens.accessToken);
    }
    else if (localStorage.getItem('user') && state.userTokens && !token) {
      /** find access token and set to send to bearer */
      const parsedSession = JSON.parse(localStorage.getItem('user') ?? '');
      setUser(parsedSession.userInfo);
      setToken(state.userTokens.accessToken);
    }
  }, [state.userTokens, state.user, token, setUser, setToken]);


  /* this will handle start date*/
  const handleStartDate = (newValue: any) => {
    if (!(newValue).isValid()) {
      setErrorStartDate("Invalid Date");
      return;
    }
    if (!localStorage.getItem("user") && moment(newValue).isBefore(moment().subtract(1, "year").subtract(12, "hour"))) {
      setErrorStartDate("Must sign in");
      return;
    }
    const newStartDate = moment(newValue).toDate();
    if (newStartDate > (endDate || new Date())) {
      setErrorStartDate("Start Date is after End Date");
    } else {
      setErrorStartDate(null);
      if(errorEndDate?.includes('before')){
        setErrorEndDate(null)
      }
    }
    setStartDate(newStartDate);
  };

  /* this will handle end date changed*/
  const handleEndDate = (newValue: any) => {
    if (!(newValue).isValid()) {
      setErrorEndDate("Invalid Date");
      return;
    }
    if (!localStorage.getItem("user") && moment(newValue).isBefore(moment().subtract(1, "year").subtract(12, "hour"))) {
      setErrorEndDate("Must sign in");
      return;
    }
    const newEndDate = moment(newValue).toDate();
    if (newEndDate < (startDate || new Date())) {
      setErrorEndDate("End Date is before Start Date");
    } else {
      setErrorEndDate(null);
      if(errorStartDate?.includes('after')){
        setErrorStartDate(null);
      }
    }
    setEndDate(newEndDate);
  };

  const tableHeader = {
    labels: ["FUEL NAME", "CATEGORY", "SUB CATEGORY", "METHOD", "LAST SAMPLE DATE"],
    keys: ["fuel_type", "category", "sub_category", "method_type", "latest_sample_date"]
  }


  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      {/* Filters */}
      <Box sx={{ flexShrink: 0, mb: 2 }}>
        <GridContainer
          className="margin-top-3 dropdown-style"
          style={{
            marginLeft: "1.5%",
            paddingBottom: "1%",
            maxWidth: "90vw",
            width: "100%",
          }}
        >

          <div>
            {/* Filters Row */}
            <Grid row gap="md">
              {/* Search Fuel Types */}
              <div style={{ width: "27%" }}>
                <label htmlFor={`fuelSearch`} id="fuelSearchLabel" className="font-sans-md text-bold">
                  Search Fuel Types
                  <div className="searchStationsDownloadPage" style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <i className="fas fa-search" style={{ fontSize: "1rem", color: "#757575", marginTop: "-0.4%", marginLeft: "0%" }} />
                    <Autocomplete
                      style={{ width: "100%", marginTop: "2%" }}
                      value={types?.[0] || null}
                      onChange={(event: any, newValue: any) => {
                        setTypes(newValue ? [newValue] : []);
                        toGetCategory(newValue);
                      }}
                      inputValue={inputType}
                      onInputChange={(event, newInputValue) => {
                        setInputType(newInputValue);
                      }}
                      id="fuelSearch"
                      options={filteredTypes.length > 0 ? filteredTypes : []}
                      getOptionLabel={(option) => option.fuel_type || ""}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Search Fuel Type"
                          InputProps={{
                            ...params.InputProps,
                            type: "search",
                            style: { height: "auto" },
                          }}
                          style={{ height: "auto" }}
                          InputLabelProps={{
                            shrink: true,
                            style: { visibility: "hidden" },
                          }}
                        />
                      )}
                      renderOption={(props, option) => (
                        <li {...props} key={option.fuel_id}>
                          {option.fuel_type}
                        </li>
                      )}
                    />
                  </div>
                </label>
              </div>
              {/* Category */}
              <div style={{ width: "15%" }}>
                <label htmlFor='categoryfuelBrowser' id="categoryfuelBrowser" className="font-sans-md text-bold">
                  Category
                </label>
                <Autocomplete
                  style={{ marginTop: "2%" }}
                  className="height-55"
                  value={category}
                  onChange={(event: any, newValue: any) => {
                    setCategory(newValue);
                  }}
                  inputValue={inputCategory}
                  onInputChange={(event, newInputValue) => {
                    setInputCategory(newInputValue);

                  }}
                  disableClearable
                  id="categoryfuelBrowser"
                  options={filteredCategories.length > 1 ? (filteredCategories) : (sampleCategories.map((option) => option.code_key))}
                  renderInput={(params) => (
                    <TextField
                      onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                      data-testid={'category'}
                      {...params}
                      InputProps={{
                        ...params.InputProps,
                        type: 'search',
                      }}
                      InputLabelProps={{
                        shrink: true,
                        style: { visibility: 'hidden' }
                      }}
                    />
                  )}
                />
              </div>

              {/* Sub-Category */}
              <div style={{ width: "15%" }}>
                <label htmlFor='subCategoryFuelBrowse' id="subCategoryFuelBrowse" className="font-sans-md text-bold">
                  Sub-Category
                </label>
                <Autocomplete
                  style={{ marginTop: "2%" }}
                  className="height-55"
                  value={subCategory}
                  onChange={(event: any, newValue: any) => {
                    setSubCategory(newValue);
                  }}
                  inputValue={inputSubCategory}
                  onInputChange={(event, newInputValue) => {
                    setInputSubCategory(newInputValue);

                  }}
                  disableClearable
                  id="subCategoryFuelBrowse"
                  options={sampleSubCats.map((option) => option?.code_key)}
                  renderInput={(params) => (
                    <TextField
                      onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                      data-testid={'sub_category'}
                      {...params}
                      InputProps={{
                        ...params.InputProps,
                        type: 'search',
                      }}
                      InputLabelProps={{
                        shrink: true,
                        style: { visibility: 'hidden' }
                      }}
                    />
                  )}
                />
              </div>

              {/* Method */}
              <div style={{ width: "15%" }}>
                <label id="methodLabel" className="font-sans-md text-bold">
                  Method
                  <Autocomplete
                    style={{ marginTop: "2%" }}
                    className="height-55"
                    value={method}
                    onChange={(event: any, newValue: any) => {
                      setMethod(newValue);
                    }}
                    inputValue={inputMethod}
                    onInputChange={(event, newInputValue) => {
                      setInputMethod(newInputValue);

                    }}
                    disableClearable
                    id="method"
                    options={sampleMethods.map((option) => option?.code_key)}
                    renderInput={(params) => (
                      <TextField
                        onInput={(e) => validateCharacters(e, ValidCharacters.TextField)}
                        data-testid={'method'}
                        {...params}
                        InputProps={{
                          ...params.InputProps,
                          type: 'search',
                        }}
                        InputLabelProps={{
                          shrink: true,
                          style: { visibility: 'hidden' }
                        }}
                      />
                    )}
                  />
                </label>
              </div>

              {/* Date Range */}
              <div style={{ width: "25%" }}>
                <span className="font-sans-md text-bold" style={{ marginBottom: "25%" }}>
                  Date Range
                </span>
                <div style={{ display: "flex", gap: "10px", marginTop: "2%" }}>
                  {/* Start Date */}
                  <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>

                  <DatePicker
                    disableFuture
                    label="Start Date"
                    value={moment(startDate).tz(state.userTimeZone.id[0])}
                    slotProps={{
                      textField: {
                        error: errorStartDate ? true : false,
                        helperText: errorStartDate,
                      },

                    }}
                    timezone={state.userTimeZone.id[0]}

                    onError={(reason) => {
                      if (reason === 'disableFuture') {
                        setErrorStartDate('Start Date is in the Future');
                      }
                      else if (reason === 'invalidDate') {
                        setErrorStartDate('Invalid Date');
                      }
                      else if (reason === null && moment(startDate) <= moment(endDate)) {
                        if(!errorEndDate){
                          setErrorEndDate(null);
                        }
                        setErrorStartDate(null);
                      }
                      else if (moment(startDate) > moment(endDate)) {
                        setErrorStartDate('Start Date is after End Date');
                      }
                      else {
                        setErrorStartDate('An Error on Start Date')
                      }
                    }}
                    onChange={(newValue) => {
                      handleStartDate(newValue);
                      setPage(0);
                    }}
                    maxDate={!localStorage.getItem('user') && moment().add(1,'day')}
                    minDate={!localStorage.getItem('user') && moment().subtract(1,'year').subtract(12,'hour')}

                  />
                  </LocalizationProvider>
                  <span style={{ margin: '0 10px' }}></span>
                  <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>

                  {/* End Date */}
                  <DatePicker
                    disableFuture
                    label="End Date"
                    value={moment(endDate).tz(state.userTimeZone.id[0])}
                    onError={(reason) => {
                      if (reason === 'disableFuture') {
                        setErrorEndDate('End Date is in the Future');
                      }
                      else if (reason === 'invalidDate') {
                        setErrorEndDate('Invalid Date');
                      }
                      else if (reason === null && moment(startDate) <= moment(endDate)) {
                        if(!errorStartDate){
                          setErrorStartDate(null);
                        }
                        setErrorEndDate(null);
                      }
                      else if (moment(startDate) > moment(endDate)) {
                        setErrorEndDate('End Date is before End Date');
                      }
                      else {
                        setErrorStartDate('An Error on Start Date')
                      }
                    }}
                    slotProps={{
                      textField: {
                        error: errorEndDate ? true : false,
                        helperText: errorEndDate,
                      },
                    }}
                    onChange={(newValue) => {
                      handleEndDate(newValue);
                      setPage(0);
                    }}
                    maxDate={!localStorage.getItem('user') && moment().add(1,'day')}
                    minDate={!localStorage.getItem('user') && moment().subtract(1,'year').subtract(12,'hour')}

                  />
                  </LocalizationProvider>

                </div>
                {!localStorage.getItem('user') &&
                  <div style={{ fontSize: '15px', fontStyle: "italic", textAlign: 'center' }}>Need more than a year of data? Please sign in</div>
                }
              </div>
            </Grid>
          </div>
        </GridContainer>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <TableContainer component={Paper} style={{
          maxHeight: "900px",
          overflow: "auto"
        }}>
          <Table style={{position: "sticky", top: 0, zIndex: 10}}>
            <TableHead style={{position: "sticky", top: 0, zIndex: 10, background: 'white'}}>
              <TableRow>
                {tableHeader.labels.map(
                  (label, index) => (
                    <TableCell key={label} style={{position: 'sticky'}}>
                      <TableSortLabel
                        active={orderBy === Object.keys(samples[0] || [])[index]}
                        direction={order}
                        onClick={(event) => handleRequestSort(event, tableHeader.keys[index] as keyof Data)}
                      >
                        {label}
                      </TableSortLabel>
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {samples
                .slice()
                .map((row, index) => (
                  <StyledTableRowBinary key={`${row.fuel_type}-${row.category}-${row.sub_category}-${row.method_type}-${row.sample}${index}`}
                    hover
                    onClick={() => {
                      handleRowClick(row)
                    }}
                    style={{
                      cursor: "pointer",
                      border: isRowSelected(row) ? "3px solid #1976d2" : "2px solid transparent",
                      borderRadius: "4px",
                    }}
                  >
                    <StyledTableCellBinary>{row.fuel_type}</StyledTableCellBinary>
                    <StyledTableCellBinary>{row.category}</StyledTableCellBinary>
                    <StyledTableCellBinary>{row.sub_category}</StyledTableCellBinary>
                    <StyledTableCellBinary>{row.method_type}</StyledTableCellBinary>
                    <StyledTableCellBinary>{row.sample}</StyledTableCellBinary>
                  </StyledTableRowBinary>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {/* Buttons */}
      <Box
        sx={{
          flexShrink: 0,
          mt: 0,
          py: 1,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <Button variant="outlined" onClick={handleClose}>
          Cancel
        </Button>
        {selectedFuel && (
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              onClick={() => {
                setCategory('All');
                setSubCategory('All');
                setMethod('All');
                setSelectedFuel(null); // Clear selection

                // Set default values
                const oneYearBefore = moment().subtract(1, "year").startOf("day").toDate();
                const today = moment().endOf("day").toDate();

                setStartDate(oneYearBefore);
                setEndDate(today);

                // Update Redux state (or global state)
                setFSMapStartDate(oneYearBefore.toISOString());
                setFSMapEndDate(today.toISOString());
              }}
            >
              Clear Selection
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => {
                setFSMapEndDate(endDate);
                setFSMapStartDate(startDate);
                handleAll("", selectedFuel['category'], selectedFuel['sub_category'], selectedFuel['fuel_type'],
                  selectedFuel['method_type']);
                handleClose();
              }}
            >
              View Fuel Type
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default FuelTable;